import { ScimSchemaService } from './ScimSchemaService';
import {
    SchemaEntityResponse,
    SchemaAttributeResponse,
    SchemaEntity,
    SchemaAttribute,
    ProcessedSchema,
    ProcessedSchemaAttribute
} from '../types/SchemaTypes';
import { debugVerboseLog } from '../utils/debugLog';

export interface SchemaData {
    schemas: any[];
    fieldOptions: any[];
    loading: boolean;
    error: string | null;
}

class CentralizedSchemaService {
    private static instance: CentralizedSchemaService;
    private schemaService: ScimSchemaService;
    private schemaData: SchemaData | null = null;
    private loadingPromise: Promise<SchemaData> | null = null;
    private subscribers: Set<(data: SchemaData) => void> = new Set();

    private constructor() {
        this.schemaService = new ScimSchemaService();
    }

    public static getInstance(): CentralizedSchemaService {
        if (!CentralizedSchemaService.instance) {
            CentralizedSchemaService.instance = new CentralizedSchemaService();
        }
        return CentralizedSchemaService.instance;
    }

    /**
     * Get schema data - returns cached data if available, otherwise starts loading
     */
    public async getSchemaData(): Promise<SchemaData> {
        // If we already have data, return it immediately
        if (this.schemaData) {
            return this.schemaData;
        }

        // If we're already loading, return the existing promise
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        // Start loading
        this.loadingPromise = this.loadSchemas();
        return this.loadingPromise;
    }

    /**
     * Subscribe to schema data updates
     */
    public subscribe(callback: (data: SchemaData) => void): () => void {
        this.subscribers.add(callback);

        // If we already have data, call the callback immediately
        if (this.schemaData) {
            callback(this.schemaData);
        }

        // Return unsubscribe function
        return () => {
            this.subscribers.delete(callback);
        };
    }

    /**
     * Check if schema data is already loaded
     */
    public isLoaded(): boolean {
        return this.schemaData !== null;
    }

    /**
     * Get cached schema data (returns null if not loaded)
     */
    public getCachedData(): SchemaData | null {
        return this.schemaData;
    }

    /**
     * Force refresh of schema data
     */
    public async refresh(): Promise<SchemaData> {
        this.schemaData = null;
        this.loadingPromise = null;
        return this.getSchemaData();
    }

    /**
     * Internal method to load schemas. DISABLED: no schema entity/attribute API calls.
     * Entity types come from tier1 entitlements; form attributes from tier2/tier3.
     * Returns empty schemas/fieldOptions so callers do not break.
     */
    private async loadSchemas(): Promise<SchemaData> {
        this.schemaData = {
            schemas: [],
            fieldOptions: [],
            loading: false,
            error: null
        };
        this.subscribers.forEach(callback => callback(this.schemaData!));
        return this.schemaData;
    }

    /**
     * Combine schema entities and attributes into unified schema structure
     */
    private combineSchemaData(schemaEntitiesResponse: SchemaEntityResponse, schemaAttributesResponse: SchemaAttributeResponse): ProcessedSchema[] {
        const entities: SchemaEntity[] = schemaEntitiesResponse.Resources || [];
        const attributes: SchemaAttribute[] = schemaAttributesResponse.Resources || [];

        // Step 1: Build attribute name → attribute info map (excluding circular Entities property)
        // Use DisplayName as the key for lookup, but store both DisplayName and Id
        const attributeInfo: Record<string, ProcessedSchemaAttribute> = attributes.reduce((acc: Record<string, ProcessedSchemaAttribute>, attr) => {
            // Use DisplayName as the property name (for matching entity properties)
            // Use Id (with $ prefix) as the identifier (for attribute references)
            const attrId = attr.Id || attr.DisplayName || '';
            const attrName = attr.DisplayName || attrId.replace(/^\$\.?/, '');

            acc[attrName] = {
                name: attrName, // Use DisplayName (without $ prefix) as the property name
                id: attrId,     // Store Id (with $ prefix) separately for attribute references
                type: attr.Type || 'string',
                description: attr.Description || `Attribute: ${attrName}`,
                required: attr.Required || false,
                multiValued: attr.Array || false,
                caseExact: attr.CaseExact || false,
                computed: attr.Computed || false,
                indexed: attr.Indexed || false,
                isReference: attr.IsReference || false,
                isReferenceTo: attr.IsReferenceTo || [],
                unique: attr.Unique || false,
                mutability: 'readonly',
                returned: 'default',
                subAttributes: []
            };
            return acc;
        }, {} as Record<string, ProcessedSchemaAttribute>);

        // Step 2: Iterate through schema entities and build entity → attributes map
        const entityAttributesMap: Record<string, ProcessedSchemaAttribute[]> = {};

        entities.forEach((entity) => {
            const entityName = entity.DisplayName;

            // Initialize empty attributes array for this entity
            entityAttributesMap[entityName] = [];

            // Only add attributes that are already defined for this entity
            if (entity.Attributes && Array.isArray(entity.Attributes)) {
                entity.Attributes.forEach((entityAttr) => {
                    // Find the full attribute definition from the attributes list
                    const fullAttribute = attributes.find(attr => attr.DisplayName === entityAttr.DisplayName);

                    if (fullAttribute) {
                        // Use DisplayName as the property name (for matching entity properties)
                        // Use Id (with $ prefix) as the identifier (for attribute references)
                        const attrId = fullAttribute.Id || fullAttribute.DisplayName || '';
                        const attrName = fullAttribute.DisplayName || attrId.replace(/^\$\.?/, '');

                        const attributeInfo = {
                            name: attrName, // Use DisplayName (without $ prefix) as the property name
                            id: attrId,     // Store Id (with $ prefix) separately for attribute references
                            type: fullAttribute.Type || 'string',
                            description: fullAttribute.Description || `Attribute: ${attrName}`,
                            required: fullAttribute.Required || false,
                            multiValued: fullAttribute.Array || false,
                            caseExact: fullAttribute.CaseExact || false,
                            computed: fullAttribute.Computed || false,
                            indexed: fullAttribute.Indexed || false,
                            isReference: fullAttribute.IsReference || false,
                            isReferenceTo: fullAttribute.IsReferenceTo || [],
                            unique: fullAttribute.Unique || false,
                            mutability: 'readonly' as const,
                            returned: 'default' as const,
                            subAttributes: []
                        };

                        entityAttributesMap[entityName].push(attributeInfo);
                    }
                });
            }
        });

        // Step 3: Create unified schema structure from entities
        const schemas: ProcessedSchema[] = entities.map((entity) => {
            const entityAttributes = entityAttributesMap[entity.DisplayName] || [];

            return {
                id: `urn:dcps:scim:v1:${entity.DisplayName}`,
                name: entity.DisplayName,
                type: 'Entity',
                description: entity.Description,
                attributes: entityAttributes,
                // Preserve the Entities property from the original schema entity, extracting just the Id values
                Entities: (entity as any).Entities ? (entity as any).Entities.map((ent: any) => ent.Id || ent) : []
            };
        });
        return schemas;
    }

    /**
     * Extract field options from schemas
     */
    private extractFieldOptions(schemas: any[]): any[] {
        const fieldOptions: any[] = [];
        const seenFields = new Set<string>();

        schemas.forEach(schema => {
            if (schema.attributes && Array.isArray(schema.attributes)) {
                schema.attributes.forEach((attr: any) => {
                    // attr.name contains DisplayName (without $ prefix) for property matching
                    // attr.id contains Id (with $ prefix) for attribute references
                    const attrName = attr.name || '';
                    const attrId = attr.id || attrName;
                    // Use Id (with $ prefix) as the unique identifier
                    if (attrId && !seenFields.has(attrId)) {
                        seenFields.add(attrId);
                        // Use attribute name (DisplayName) for labels
                        fieldOptions.push({
                            name: attrName, // Property name (without $ prefix) for entity property matching
                            id: attrId,     // Id (with $ prefix) for attribute references
                            value: attrId,  // Use Id (with $ prefix) as the value
                            label: attrName, // Display attribute name (DisplayName without $ prefix)
                            type: attr.type || 'string',
                            required: attr.required || false,
                            multiValued: attr.multiValued || false,
                            isReference: attr.isReference || false
                        });
                    }
                });
            }
        });

        return fieldOptions.sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * Get field options for a specific entity type
     */
    public async getFieldOptionsForEntityType(entityType: string): Promise<any[]> {
        debugVerboseLog(`🔍 CentralizedSchemaService: Getting field options for entity type: ${entityType} (type: ${typeof entityType})`);

        // Type guard to ensure entityType is a string
        if (typeof entityType !== 'string') {
            console.error(`❌ CentralizedSchemaService: entityType is not a string:`, entityType);
            return [];
        }

        const schemaData = await this.getSchemaData();

        if (schemaData.error) {
            debugVerboseLog(`❌ CentralizedSchemaService: Schema data has error: ${schemaData.error}`);
            return [];
        }

        // Find the schema for this entity type (case-insensitive)
        const schema = schemaData.schemas.find((s: any) => {
            const schemaId = s.id?.toLowerCase() || '';
            const searchType = entityType.toLowerCase();
            return schemaId === searchType ||
                schemaId === `urn:dcps:scim:v1:${searchType}` ||
                schemaId === `urn:ietf:params:scim:schemas:core:2.0:${searchType}` ||
                schemaId.includes(searchType);
        });

        if (!schema || !schema.attributes) {
            debugVerboseLog(`⚠️ CentralizedSchemaService: No schema found for entity type: ${entityType}`);
            debugVerboseLog(`🔍 CentralizedSchemaService: Available schemas:`, schemaData.schemas.map((s: any) => s.id));
            return [];
        }

        const fieldOptions = schema.attributes.map((attr: any) => {
            // attr.name contains DisplayName (without $ prefix) for property matching
            // attr.id contains Id (with $ prefix) for attribute references
            const attrName = attr.name || '';
            const attrId = attr.id || attrName;

            return {
                name: attrName, // Property name (without $ prefix) for entity property matching
                id: attrId,     // Id (with $ prefix) for attribute references
                value: attrId,  // Use Id (with $ prefix) as the value
                label: attrName, // Display attribute name (DisplayName without $ prefix)
                type: attr.type || 'string',
                required: attr.required || false,
                multiValued: attr.multiValued || false,
                isReference: attr.isReference || false
            };
        });

        debugVerboseLog(`✅ CentralizedSchemaService: Found ${fieldOptions.length} field options for ${entityType}`);
        return fieldOptions;
    }

    /**
     * Get all schemas
     */
    public async getSchemas(): Promise<any[]> {
        const schemaData = await this.getSchemaData();
        return schemaData.schemas;
    }

    /**
     * Get all field options
     */
    public async getFieldOptions(): Promise<any[]> {
        const schemaData = await this.getSchemaData();
        return schemaData.fieldOptions;
    }
}

export default CentralizedSchemaService;
